// =====================================================================
// PropertyCuts — paid scene jobs (ImageMotion scenes, VideoMotion walkthroughs)
// =====================================================================
// All pricing, charging and provider calls happen in the `propertycut-jobs`
// edge function. The browser only frames the still it wants animated and
// reads job status.
// =====================================================================

import type { RenderFormat } from '../types';
import { STORAGE_BUCKET } from '../constants';
import { supabase } from './supabase';

export type JobStatus = 'queued' | 'processing' | 'ready' | 'failed';

export interface PropertyCutJob {
  id: string;
  kind: 'imagemotion_scene' | 'videomotion_walkthrough';
  project_id: string;
  scene_id: string | null;
  source_asset_ids: string[];
  result_asset_ids: string[];
  status: JobStatus;
  aspect_ratio: string | null;
  duration_seconds: number | null;
  credit_cost: number;
  charged: boolean;
  refunded: boolean;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface JobQuote {
  balance: number;
  scene: { credits: number | null; seconds: number; available: boolean; reason: string | null };
  walkthrough: { credits: number | null; maxClips: number; maxClipSeconds: number; available: boolean; reason?: string };
}

export class JobRequestError extends Error {
  code: string | null;
  credits: number | null;
  constructor(message: string, code: string | null = null, credits: number | null = null) {
    super(message);
    this.code = code;
    this.credits = credits;
  }
}

export function isActiveJob(job: Pick<PropertyCutJob, 'status'>): boolean {
  return job.status === 'queued' || job.status === 'processing';
}

async function callJobs<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('propertycut-jobs', { body: { action, ...body } });
  if (!error) return data as T;
  let payload: { error?: string; code?: string; credits?: number } = {};
  const response = (error as { context?: Response }).context;
  if (response && typeof response.json === 'function') {
    payload = await response.json().catch(() => ({}));
  }
  throw new JobRequestError(payload.error || 'Something went wrong. Try again.', payload.code ?? null, payload.credits ?? null);
}

export function fetchJobQuote(projectId: string): Promise<JobQuote> {
  return callJobs<JobQuote>('quote', { projectId });
}

export async function pollJobs(projectId: string): Promise<PropertyCutJob[]> {
  const data = await callJobs<{ jobs: PropertyCutJob[] }>('poll', { projectId });
  return data.jobs ?? [];
}

export async function startSceneAnimation(args: {
  projectId: string;
  sceneId: string;
  sourceAssetId: string;
  framedPath: string | null;
  expectedCredits: number;
  idempotencyKey: string;
}): Promise<PropertyCutJob> {
  const data = await callJobs<{ job: PropertyCutJob }>('animate', args);
  return data.job;
}

export async function startWalkthrough(args: {
  projectId: string;
  assetIds: string[];
  expectedCredits: number;
  idempotencyKey: string;
}): Promise<PropertyCutJob> {
  const data = await callJobs<{ job: PropertyCutJob }>('walkthrough', args);
  return data.job;
}

export function newIdempotencyKey(prefix: string): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${random}`;
}

export function formatAspect(format: RenderFormat): { width: number; height: number } {
  if (format === 'vertical') return { width: 9, height: 16 };
  if (format === 'square') return { width: 1, height: 1 };
  return { width: 16, height: 9 };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the photo'));
    img.src = url;
  });
}

/**
 * Crop a photo to the video's aspect ratio exactly as the preview shows a
 * "Crop to fill" scene (object-fit: cover + object-position from the offsets),
 * so the animation starts from the framing the user saw.
 */
export async function frameStill(
  url: string,
  format: RenderFormat,
  offsetX: number,
  offsetY: number,
): Promise<Blob> {
  const img = await loadImage(url);
  const { width: aw, height: ah } = formatAspect(format);
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const target = aw / ah;
  const cropW = iw / ih > target ? ih * target : iw;
  const cropH = iw / ih > target ? ih : iw / target;
  const px = (offsetX + 1) / 2;
  const py = (1 - offsetY) / 2;
  const sx = (iw - cropW) * px;
  const sy = (ih - cropH) * py;

  const scale = Math.min(1, 2048 / Math.max(cropW, cropH));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cropW * scale);
  canvas.height = Math.round(cropH * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare the photo');
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not prepare the photo'))), 'image/jpeg', 0.92);
  });
}

/** Upload a framed still next to the project's assets; returns its storage path. */
export async function uploadFramedStill(userId: string, projectId: string, sceneId: string, blob: Blob): Promise<string> {
  const path = `assets/${userId}/${projectId}/framed-${sceneId}-${Date.now()}.jpg`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    upsert: false,
  });
  if (error) throw new Error(error.message || 'Could not upload the framed photo');
  return path;
}
